import { DocumentNode, gql } from '@apollo/client';
import {
  Avatar,
  Box,
  Checkbox,
  Hide,
  Td,
  Text,
  Tooltip,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { resource_viewed } from '@/analytics';
import { FileNameTooltipLabel } from '@/components/FileNameTooltipLabel';
import { DeleteLibraryResourceModal } from '@/components/LibraryResourceModals/DeleteLibraryResourceModal';
import { RenameLibraryResourceModal } from '@/components/LibraryResourceModals/RenameLibraryResourceModal';
import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { PREVIEW_RENDERER_VERSION_FILE_FRAGMENT } from '@/components/PreviewRenderer/PreviewRendererFragment';
import { getFileTypeDisplayInfo } from '@/imports/ui/UtilsNoVulcan';
import { BulkMoveLibraryFilesModal } from '@/imports/ui/components/resource/BulkMoveLibraryFilesModal';
import { ResourceListDragHandle } from '@/imports/ui/components/resource/ResourceListDragHandle';
import {
  ResourceListName,
  resourceListDateTdProps,
  resourceListNameTdProps,
  resourceListNameTextProps,
} from '@/imports/ui/components/resource/ResourceListName';
import { ISelectionState } from '@/imports/ui/useSelectionState';
import {
  DraggableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd';

import { ResourceListItem_VersionFileFragment } from './graphql-types/ResourceListItem_VersionFileFragment';

import { FileItemDropdown } from './components/FileItemDropdown';

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer/PreviewRenderer').then(
      (mod) => mod.PreviewRenderer
    ),
  {
    ssr: false,
  }
);

type IVersionFile = ResourceListItem_VersionFileFragment;

const getResourceType = (
  fileType: string
): 'pdf' | 'doc' | 'video' | 'image' | 'other' => {
  if (fileType.includes('pdf')) return 'pdf';
  if (fileType.includes('word') || fileType.includes('document')) return 'doc';
  if (fileType.includes('video')) return 'video';
  if (fileType.includes('image')) return 'image';
  return 'other';
};

type IResourceListItemProps = {
  versionFile: IVersionFile | null;
  selection: ISelectionState<{ _id: string }>;
  toggleOne: (item: { _id: string }) => void;
  canEditGroupLibrary: boolean;
  groupId: string;
  rootFolderId: string;
  onAfterDelete: () => void;
  onAfterMove: () => void;
  isReorderEnabled?: boolean;
  dragOptions?: {
    provided: DraggableProvided;
    snapshot: DraggableStateSnapshot;
  };
};

export const ResourceListItem: React.FC<IResourceListItemProps> & {
  versionFileFragment: DocumentNode;
} = ({
  versionFile,
  selection,
  toggleOne,
  canEditGroupLibrary,
  groupId,
  rootFolderId,
  onAfterDelete,
  onAfterMove,
  isReorderEnabled = false,
  dragOptions,
}) => {
  const moveFileModal = useDisclosure();
  const deleteFileModal = useDisclosure();
  const renameFileModal = useDisclosure();
  const router = useRouter();

  if (!versionFile?.file) return null;

  const currentVersionFile = versionFile;
  const currentFile = versionFile.file;

  const { ChakraIcon, previewDisabled } = getFileTypeDisplayInfo(
    currentFile.type
  );

  const openFilePreviewModal = () => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          versionFileId: versionFile?._id.toString(),
        },
      },
      undefined,
      { shallow: true }
    );

    resource_viewed({
      entity_type: 'resource',
      entity_id: currentVersionFile._id,
      resource_id: currentVersionFile._id,
      resource_name: currentFile.name,
      resource_type: getResourceType(currentFile.type),
      location_group_id: groupId,
    });
  };

  return (
    <Tr
      ref={dragOptions?.provided.innerRef}
      {...dragOptions?.provided.draggableProps}
      _hover={{ bg: 'gray.50' }}
      minH="4em"
      bg={dragOptions?.snapshot.isDragging ? 'blue.50' : undefined}
    >
      {canEditGroupLibrary && (
        <Td w="8" pr="0">
          <ResourceListDragHandle
            isEnabled={isReorderEnabled}
            dragHandleProps={dragOptions?.provided.dragHandleProps}
          />
        </Td>
      )}
      {canEditGroupLibrary && (
        <Td text-align="center">
          <Checkbox
            isChecked={
              (selection.allSelected &&
                !selection.excludedItems
                  .map((item) => item._id)
                  .includes(versionFile._id)) ||
              (!selection.allSelected &&
                selection.includedItems
                  .map((item) => item._id)
                  .includes(versionFile._id))
            }
            onChange={() => toggleOne(versionFile)}
            boxSize={6}
          />
        </Td>
      )}
      <Td {...resourceListNameTdProps}>
        {router.query.versionFileId === versionFile._id && (
          <PreviewRendererModal
            versionFile={versionFile}
            onClose={() => {
              const { versionFileId, ...query } = router.query;
              router.replace(
                { pathname: router.pathname, query: query },
                undefined,
                { shallow: true }
              );
            }}
            isOpen={router.query.versionFileId === versionFile._id}
            linkOriginalPost
          />
        )}

        <DeleteLibraryResourceModal
          onClose={deleteFileModal.onClose}
          isOpen={deleteFileModal.isOpen}
          groupId={groupId}
          versionFileIds={[versionFile?._id as string]}
          onAfterDeleteFiles={onAfterDelete}
        />

        <BulkMoveLibraryFilesModal
          groupId={groupId}
          versionFileIds={[versionFile?._id as string]}
          open={moveFileModal.isOpen}
          onClose={moveFileModal.onClose}
          rootFolderId={rootFolderId}
          onAfterMove={onAfterMove}
        />

        <RenameLibraryResourceModal
          isOpen={renameFileModal.isOpen}
          onClose={renameFileModal.onClose}
          versionFileId={versionFile._id}
          initialName={versionFile.file.name}
        />
        <Tooltip
          hasArrow
          label={
            <FileNameTooltipLabel
              previewDisabled={!!previewDisabled}
              fileName={versionFile.file.name}
              fileNameLengthThreshold={versionFile.file.name.length}
            />
          }
          isDisabled={!previewDisabled}
        >
          <ResourceListName
            icon={<ChakraIcon boxSize={6} />}
            onClick={!!previewDisabled ? undefined : openFilePreviewModal}
            cursor={!!previewDisabled ? undefined : 'pointer'}
          >
            <Text textStyle="compactStrong" {...resourceListNameTextProps}>
              {versionFile.file.name}
            </Text>
          </ResourceListName>
        </Tooltip>
      </Td>
      <Hide below="lg">
        <Td {...resourceListDateTdProps}>
          <Text textStyle="compact">
            {dayjs(versionFile.dateCreated).format('MMM D, YYYY')}
          </Text>
        </Td>
        <Td>
          <Tooltip label={versionFile.author.displayName} hasArrow>
            <Box display="inline-block" pl="2">
              <NextLink href={`/members/${versionFile.author._id}`}>
                <Avatar
                  size="sm"
                  name={versionFile.author.displayName}
                  src={versionFile.author.proxyProfilePictureUrl || undefined}
                />
              </NextLink>
            </Box>
          </Tooltip>
        </Td>
      </Hide>
      <Td>
        <FileItemDropdown
          versionFile={versionFile}
          onOpenDeleteFile={deleteFileModal.onOpen}
          onOpenMoveFile={moveFileModal.onOpen}
          onOpenRenameFile={renameFileModal.onOpen}
          onOpenPreviewFile={openFilePreviewModal}
          previewDisabled={!!previewDisabled}
          canEditGroupLibrary={canEditGroupLibrary}
        />
      </Td>
    </Tr>
  );
};

ResourceListItem.versionFileFragment = gql`
  fragment ResourceListItem_VersionFileFragment on VersionFile {
    _id
    dateCreated
    originalPost {
      _id
    }
    originalComment {
      _id
    }
    author {
      _id
      displayName
      proxyProfilePictureUrl(
        input: { w: 40, h: 40, fit: "facearea", facepad: 10.0 }
      )
    }
    file {
      _id
      name
      url
      type
    }
    ...PreviewRenderer_VersionFileFragment
    ...FileItemDropdown_VersionFileFragment
  }
  ${PREVIEW_RENDERER_VERSION_FILE_FRAGMENT}
  ${FileItemDropdown.versionFileFragment}
`;
